import { FC, useMemo, useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TrendingDown, TrendingUp } from "lucide-react";
import type { ShopifyMetric, SparkPoint } from "../ShopifyGeoSpatialDashboard.data";
import DefinitionTooltip from "src/components/common/DefinitionTooltip";
import { useLanguage } from "src/hooks/UseLanguage";
import {
  type HoverState,
  formatGeoDate,
  geoNumberText,
  getFixedTooltipPosition,
  getGeoMetricDefinition,
  getGeoMetricDisplayTitle,
  MAX_VERTEX_MARKERS,
  normalizeGeoMetricKey,
  sparkPoints,
  toPolyline,
} from "./ShopifyGeoSpatialDashboard.utils";
import "../../../styles/ShopifyGeoSparklineMetric.scss";

const ShopifyGeoSparklineMetric: FC<{
  metric: ShopifyMetric;
  scopeLabel: string;
  selected?: boolean;
  onSelectKpi?: () => void;
}> = ({ metric, scopeLabel, selected, onSelectKpi }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const spark = metric.spark;
  const max = useMemo(
    () => Math.max(...spark.map(p => p.current), ...spark.map(p => p.previous), 1),
    [spark]
  );
  const currentPoints = useMemo(() => sparkPoints(spark, "current", max), [spark, max]);
  const previousPoints = useMemo(() => sparkPoints(spark, "previous", max), [spark, max]);
  const showVertexMarkers = spark.length > 1 && spark.length <= MAX_VERTEX_MARKERS;
  const isIncrease = metric.delta >= 0;
  const normalizedKey = normalizeGeoMetricKey(metric.key);
  const normalizedTitle = normalizeGeoMetricKey(metric.title);
  const isInvertedMetric =
    normalizedKey === "discount" ||
    normalizedKey === "returns" ||
    normalizedKey === "refunds" ||
    normalizedTitle === "returncount" ||
    normalizedTitle === "returns" ||
    normalizedTitle === "refunds";
  const isPositive = isInvertedMetric ? !isIncrease : isIncrease;
  const [hover, setHover] = useState<HoverState | null>(null);
  const metricTitle = getGeoMetricDisplayTitle(metric, t);
  const metricDefinition = getGeoMetricDefinition(metric, scopeLabel, t);

  const formatHoverValue = (value: number) => {
    const rounded = geoNumberText(Math.round(value).toLocaleString(), isRTL);
    if (metric.value.includes("PKR")) {
      return t("shopifyGeo.spark.pkrValue", { value: rounded });
    }
    if (metric.key === "orders") {
      return `${rounded} ${t("shopifyGeo.spark.orders")}`;
    }
    return rounded;
  };

  const handleSparkMove = (event: MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }

    const normalizedX = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
    const normalizedY = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
    const index = Math.max(
      0,
      Math.min(spark.length - 1, Math.round((normalizedX / 100) * (spark.length - 1)))
    );

    const currentDistance = Math.abs(normalizedY - currentPoints[index].y);
    const previousDistance = Math.abs(normalizedY - previousPoints[index].y);
    const period = currentDistance <= previousDistance ? "current" : "previous";

    setHover({ index, period, clientX: event.clientX, clientY: event.clientY });
  };

  const activePoint = hover ? spark[hover.index] : null;

  const bucketDateLabel = (point: SparkPoint, period: "current" | "previous") => {
    if (period === "current") {
      return point.current_start_date ?? point.label;
    }
    return point.previous_start_date ?? point.label;
  };

  const sparkPointDisplayValue = (point: SparkPoint, period: "current" | "previous") => {
    const formatted = period === "current" ? point.currentFormattedValue : point.previousFormattedValue;
    return formatted ?? formatHoverValue(point[period]);
  };

  return (
    <article
      className={`shopify-geo__metric-card${onSelectKpi ? " shopify-geo__metric-card--selectable" : ""}${selected ? " shopify-geo__metric-card--active" : ""}`}
      aria-label={metricTitle}
      role={onSelectKpi ? "button" : undefined}
      tabIndex={onSelectKpi ? 0 : undefined}
      onClick={onSelectKpi ? () => onSelectKpi() : undefined}
      onKeyDown={
        onSelectKpi
          ? e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelectKpi();
              }
            }
          : undefined
      }
    >
      <div
        className="shopify-geo__metric-title-row"
        onClick={onSelectKpi ? e => e.stopPropagation() : undefined}
        onKeyDown={onSelectKpi ? e => e.stopPropagation() : undefined}
      >
        <p className="shopify-geo__metric-title">{metricTitle}</p>
        <DefinitionTooltip
          label={metricTitle}
          info={metricDefinition}
          preferSide={isRTL ? "left" : "right"}
          formulaTitle={t("shopifyGeo.tooltips.formulaTitle")}
          exampleLabel={t("shopifyGeo.tooltips.exampleLabel")}
        />
      </div>
      <p className="shopify-geo__metric-value">{metric.value}</p>
      <p className={`shopify-geo__metric-delta ${isPositive ? "shopify-geo__metric-delta--positive" : "shopify-geo__metric-delta--negative"}`}>
        {isIncrease ? <TrendingUp size={12} aria-hidden /> : <TrendingDown size={12} aria-hidden />}{" "}
        {metric.deltaLabel}
      </p>
      <div
        className="shopify-geo__spark-wrap"
        onClick={onSelectKpi ? e => e.stopPropagation() : undefined}
        onKeyDown={onSelectKpi ? e => e.stopPropagation() : undefined}
      >
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
          onMouseMove={handleSparkMove}
          onMouseLeave={() => setHover(null)}
        >
          <polyline
            points={toPolyline(previousPoints)}
            className="shopify-geo__spark-line shopify-geo__spark-line--previous"
            fill="none"
            stroke="#94d0b5"
            strokeWidth={2}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          <polyline
            points={toPolyline(currentPoints)}
            className="shopify-geo__spark-line shopify-geo__spark-line--current"
            fill="none"
            stroke="#2b74e4"
            strokeWidth={2}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          {showVertexMarkers &&
            previousPoints.map((pt, i) => (
              <circle
                key={`spark-vp-${i}`}
                cx={pt.x}
                cy={pt.y}
                r={1.35}
                className="shopify-geo__spark-vertex shopify-geo__spark-vertex--previous"
                fill="rgba(148, 208, 181, 0.32)"
                stroke="#94d0b5"
                strokeWidth={0.35}
              />
            ))}
          {showVertexMarkers &&
            currentPoints.map((pt, i) => (
              <circle
                key={`spark-vc-${i}`}
                cx={pt.x}
                cy={pt.y}
                r={1.35}
                className="shopify-geo__spark-vertex shopify-geo__spark-vertex--current"
                fill="#2b74e4"
                stroke="#8d4fd3"
                strokeWidth={0.35}
              />
            ))}
          {hover && (
            <>
              <circle
                cx={previousPoints[hover.index].x}
                cy={previousPoints[hover.index].y}
                r={hover.period === "previous" ? 4 : 2.7}
                className="shopify-geo__spark-dot shopify-geo__spark-dot--previous"
                fill="rgba(148, 208, 181, 0.22)"
                stroke="#94d0b5"
                strokeWidth={1.5}
              />
              <circle
                cx={currentPoints[hover.index].x}
                cy={currentPoints[hover.index].y}
                r={hover.period === "current" ? 4 : 2.7}
                className="shopify-geo__spark-dot shopify-geo__spark-dot--current"
                fill="#2b74e4"
                stroke="#8d4fd3"
                strokeWidth={1.5}
              />
            </>
          )}
        </svg>
        {hover && activePoint && createPortal(
          <div
            className="analytics-chart-tooltip analytics-chart-tooltip--shopify-kpi"
            style={getFixedTooltipPosition(hover.clientX, hover.clientY, 250, 66, 12, 8, isRTL)}
          >
            <div className="analytics-chart-tooltip__bar" />
            <div className="analytics-chart-tooltip__content">
              <span className="analytics-chart-tooltip__meta">
                {hover.period === "current"
                  ? t("shopifyGeo.tooltips.currentPeriodDate")
                  : t("shopifyGeo.tooltips.previousPeriodDate")}{" "}
                <strong>
                  {geoNumberText(formatGeoDate(bucketDateLabel(activePoint, hover.period)), isRTL)}
                </strong>
              </span>
              <span className="analytics-chart-tooltip__meta">
                {metricTitle}: <strong>{sparkPointDisplayValue(activePoint, hover.period)}</strong>
              </span>
            </div>
          </div>,
          document.body,
        )}
      </div>
    </article>
  );
};

export default ShopifyGeoSparklineMetric;
